import { get, isEqual } from 'lodash'
import qs from 'qs'
import { useMemo } from 'react'
import { useSearchParams as useRouterSearchParams } from 'react-router-dom'
import z from 'zod'

export interface UseSearchParamsProps<S extends z.ZodObject> {
  schema: S
  initialState: z.infer<S>
  nullableKeys?: Array<keyof z.infer<S>>
}

// Note: query keys not owned by this hook's schema (e.g. a parent route's `?tab=`,
// when this page's filters are embedded inside a bigger page) must survive writes/clears.
const stripOwnKeys = (params: URLSearchParams, ownKeys: Set<string>) => {
  const keysToDelete: string[] = []
  params.forEach((_, key) => {
    // NOTE: qs serializes arrays/objects as `key[0]`, `key[nested]`, etc.
    const baseKey = key.split('[')[0]
    if (ownKeys.has(baseKey)) keysToDelete.push(key)
  })
  keysToDelete.forEach((key) => params.delete(key))
}

export const useSearchParams = <S extends z.ZodObject>({
  schema,
  initialState,
}: UseSearchParamsProps<S>) => {
  const [searchParams, setSearchParams] = useRouterSearchParams()

  const ownKeys = useMemo(() => new Set(Object.keys(schema.shape)), [schema])

  const parsedQuery = useMemo(() => {
    const queryObject = qs.parse(searchParams.toString())

    try {
      return { ...initialState, ...schema.parse(queryObject) }
    } catch (error) {
      console.warn('Failed to parse query parameters with schema:', error)
      return initialState
    }
  }, [searchParams])

  const writeParams = (filters: z.infer<S>) => {
    const queryString = qs.stringify(filters, {
      filter: (prefix, value) => {
        // NOTE: we don't want to see in URL query param values that are equal to initial state ones
        if (prefix && isEqual(get(initialState, prefix), value)) return undefined
        return value
      },
    })

    const mergedParams = new URLSearchParams(searchParams)
    stripOwnKeys(mergedParams, ownKeys)
    new URLSearchParams(queryString).forEach((value, key) => mergedParams.append(key, value))

    setSearchParams(mergedParams, { replace: true })
  }

  const clearParams = () => {
    const mergedParams = new URLSearchParams(searchParams)
    stripOwnKeys(mergedParams, ownKeys)
    setSearchParams(mergedParams, { replace: true })
  }

  return {
    clearParams,
    parsedQuery,
    searchParams,
    writeParams,
  }
}
